import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { ActivityIndicator, Platform, Pressable, View } from "@-/com.npmjs.react-native";
import * as AppleAuthentication from "@-/com.npmjs.expo-apple-authentication";
import * as Linking from "@-/com.npmjs.expo-linking";
import { PostHogMaskView } from "@-/com.npmjs.posthog-react-native";
import { useMutation, useQueryClient } from "@-/com.npmjs.tanstack__react-query";
import { captureAnalyticsEvent, noteAuthMethod } from "@-/dev.improve.haniverse.lib.analytics";
import { getAuthClient, interactiveSignInDeadline } from "@-/dev.improve.haniverse.lib.auth.client";
import { useNetworkOnline } from "@-/dev.improve.haniverse.lib.query-client";
import { useDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";
import { Button } from "@-/dev.improve.haniverse.components.ui.button";
import { Input } from "@-/dev.improve.haniverse.components.ui.input";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { isDeadlineExceededError, withDeadline } from "@-/dev.improve.haniverse.lib.deadline";
import { finishGetStartedSocialSignIn } from "#finish-get-started-social-sign-in";
import { isPlausibleEmail } from "#is-plausible-email";

export type GetStartedAuthMode = "signIn" | "signUp";

/**
 * Confirming the session after the provider returns is one get-session round trip. On expiry the
 * button is released with a retry message; the session effect still leaves the moment Better Auth
 * publishes the session, so a late answer is not lost.
 */
const SESSION_CONFIRM_DEADLINE_MS = 15_000;

/**
 * The account entry options: Apple, Google, and email. Used by the auth sheet (both its Log In and
 * Sign Up modes) and the first-level prompt.
 *
 * Social sign-in creates or opens a real account immediately, so the same buttons serve both
 * logging in and signing up — only the verb changes (`Log in with` vs `Continue with`). On web,
 * email uses the same outline button style; on mobile it stays a de-emphasized underlined link so
 * Google/Apple remain the preferred path. Pressing either reveals the email fields in place. The
 * default email path records the address as the device's account email and nothing else: no
 * request and no account yet, so it works offline and the app is usable immediately. The user stays
 * an anonymous local account until they sign up with that address and a password (the account
 * drawer's "Email needs verification" badge opens that form with the address filled in) and confirm
 * the emailed code. Hosts that need password fields instead supply `renderEmailForm`.
 */
export function GetStartedAuth({
  emailFormMinHeight,
  isEmailRevealed,
  mode = "signUp",
  onContinue,
  onRevealEmail,
  renderEmailForm,
  socialOnly = false,
}: {
  /** Reserves height for the revealed email fields, so hosts can match sibling layouts. */
  emailFormMinHeight?: number;
  /** Controls whether the email fields replace the email control. Uncontrolled when omitted. */
  isEmailRevealed?: boolean;
  /**
   * `signIn` → "Log in with …"; `signUp` (default) → "Continue with …". Email uses the same verb.
   */
  mode?: GetStartedAuthMode;
  /** Called after a social session exists OR the email was recorded locally. */
  onContinue: () => void;
  /** Called by the email control when `isEmailRevealed` is controlled. */
  onRevealEmail?: () => void;
  /** Replaces the local-only email + Continue fields once the email control is pressed. */
  renderEmailForm?: () => ReactNode;
  /** Account settings offers provider connection without another email entry. */
  socialOnly?: boolean;
}) {
  const online = useNetworkOnline();
  const queryClient = useQueryClient();
  const { data: session } = getAuthClient().useSession();
  const hasLeft = useRef(false);
  const leave = useCallback(() => {
    if (hasLeft.current) {
      return;
    }
    hasLeft.current = true;
    onContinue();
  }, [onContinue]);
  const finishSocialSignIn = useCallback(
    (email: string) => {
      if (hasLeft.current) {
        return;
      }
      finishGetStartedSocialSignIn(queryClient, email);
      leave();
    },
    [leave, queryClient],
  );
  // Better Auth can publish the session before the browser flow resolves. Leave
  // immediately; the Learn card owns the remaining account-data loading state.
  const sessionEmail = session?.user.email;
  useEffect(() => {
    if (sessionEmail != null) {
      finishSocialSignIn(sessionEmail);
    }
  }, [finishSocialSignIn, sessionEmail]);
  const [accountEmail = "", setAccountEmail] = useDeviceSetting("accountEmail");
  const [showEmail, setShowEmail] = useState(false);
  const [email, setEmail] = useState(accountEmail ?? "");
  const [message, setMessage] = useState<string | null>(null);
  const socialMutation = useMutation({
    // The person may stay in the provider's sheet as long as they like; the sheet reports success
    // or cancellation. Only the server round trips are timed.
    meta: { deadline: interactiveSignInDeadline() },
    mutationFn: async (provider: "apple" | "google") => {
      setMessage(null);
      if (!online) {
        throw new Error(
          socialOnly
            ? "Connect to the internet to connect Apple or Google."
            : "Connect to the internet to use Apple or Google. You can continue with email offline.",
        );
      }
      let idToken: { token: string } | undefined;
      if (provider === "apple" && Platform.OS === "ios") {
        const credential = await AppleAuthentication.signInAsync({
          requestedScopes: [
            AppleAuthentication.AppleAuthenticationScope.FULL_NAME,
            AppleAuthentication.AppleAuthenticationScope.EMAIL,
          ],
        });
        if (!credential.identityToken) {
          throw new Error("Apple did not return a sign-in token.");
        }
        idToken = { token: credential.identityToken };
      }
      noteAuthMethod(provider);
      const result = await getAuthClient().signIn.social({
        // Web's root is the marketing page. OAuth reloads the document, so enter app startup
        // directly instead of relying on this form's onContinue handler surviving the redirect.
        callbackURL: Linking.createURL(Platform.OS === "web" ? "/startup" : "/"),
        ...(idToken ? { idToken } : {}),
        provider,
      });
      if (result.error) {
        throw new Error(result.error.message ?? "Could not sign in. Please try again.");
      }
      // Cancelling the browser is not a successful sign-in.
      if (hasLeft.current) {
        return;
      }
      const confirmedSession = await withDeadline(
        { deadline: SESSION_CONFIRM_DEADLINE_MS, operation: "confirm social sign-in session" },
        async () => getAuthClient().getSession(),
      );
      if (confirmedSession.data?.user) {
        finishSocialSignIn(confirmedSession.data.user.email);
      }
    },
    mutationKey: ["get-started-social"],
    networkMode: "always",
    onError: (error) => {
      if ("code" in error && error.code === "ERR_REQUEST_CANCELED") {
        return;
      }
      // Only a server round trip has a deadline here, so an expiry means the server never answered.
      setMessage(
        isDeadlineExceededError(error)
          ? "Haniverse isn't responding right now. Please try again."
          : error.message,
      );
    },
  });
  const continueWithEmailMutation = useMutation({
    mutationFn: async () => {
      setMessage(null);
      const address = email.trim();
      if (!isPlausibleEmail(address)) {
        throw new Error("Enter a valid email address.");
      }
      // Deliberately LOCAL ONLY — nothing is sent and no account exists yet. The address is
      // verified later, when the user signs up with it, so a typo costs nothing here.
      const hadPreviousEmail = Boolean(accountEmail);
      setAccountEmail(address);
      // Never the address itself: it stays on this device until the user signs up with it.
      captureAnalyticsEvent("onboarding_email_submitted", { had_previous_email: hadPreviousEmail });
      await Promise.resolve();
      leave();
    },
    mutationKey: ["get-started-continue-with-email"],
    networkMode: "always",
    onError: (error) => setMessage(error.message),
  });
  // App Store guideline 5.1.1(v): studying needs no account, so no personal information is required
  // to enter the app. An empty address is a local profile without an email; the learner can add one,
  // or connect Apple or Google, later from Account settings.
  const continueWithoutAccountMutation = useMutation({
    mutationFn: async () => {
      setMessage(null);
      if (!accountEmail) {
        setAccountEmail("");
      }
      captureAnalyticsEvent("onboarding_continued_without_account", {});
      await Promise.resolve();
      leave();
    },
    mutationKey: ["get-started-continue-without-account"],
    networkMode: "always",
    onError: (error) => setMessage(error.message),
  });
  const pending =
    socialMutation.isPending ||
    continueWithEmailMutation.isPending ||
    continueWithoutAccountMutation.isPending;
  const applePending = socialMutation.isPending && socialMutation.variables === "apple";
  const googlePending = socialMutation.isPending && socialMutation.variables === "google";
  const isEmailShown = isEmailRevealed ?? showEmail;
  const withProvider = socialOnly ? "Connect" : mode === "signIn" ? "Log in with" : "Continue with";
  const revealEmail = () => {
    if (onRevealEmail === undefined) {
      setShowEmail(true);
    } else {
      onRevealEmail();
    }
  };
  const emailLabel = `${online ? withProvider : "Continue with"} email`;

  return (
    <View className="w-full gap-3">
      {Platform.OS === "ios" ? (
        <Button
          accessibilityState={{
            busy: applePending,
            disabled: pending && !applePending,
          }}
          className="h-12 rounded-full"
          // Keep the in-flight button at full opacity so the spinner stays readable; only the
          // sibling is hard-disabled. `isLoading` already blocks a second press.
          disabled={pending && !applePending}
          isLoading={applePending}
          onPress={() => socialMutation.mutate("apple")}
          testID="get-started-apple"
          variant="outline"
        >
          {applePending ? (
            <ActivityIndicator size="small" />
          ) : (
            <Icon className="text-foreground" name="AiFillApple" size={22} />
          )}
          <Text>{withProvider} Apple</Text>
        </Button>
      ) : null}
      <Button
        accessibilityState={{
          busy: googlePending,
          disabled: pending && !googlePending,
        }}
        className="h-12 rounded-full"
        disabled={pending && !googlePending}
        isLoading={googlePending}
        onPress={() => socialMutation.mutate("google")}
        testID="get-started-google"
        variant="outline"
      >
        {googlePending ? (
          <ActivityIndicator size="small" />
        ) : (
          <Icon className="text-foreground" name="FaGoogle" size={18} />
        )}
        <Text>{withProvider} Google</Text>
      </Button>
      {!online ? (
        <View className="rounded-xs bg-muted p-3" testID="auth-offline-note">
          <Text className="text-sm text-muted-foreground">
            {socialOnly
              ? "You're offline. Your progress stays on this device. Connect Apple or Google here when you're back online."
              : "You're offline. Start with your email now and connect Apple or Google later in Account settings. Your progress stays on this device until you connect."}
          </Text>
        </View>
      ) : null}
      {socialOnly ? null : isEmailShown ? (
        <View style={{ minHeight: emailFormMinHeight }}>
          {(online ? renderEmailForm?.() : undefined) ?? (
            <View className="gap-3">
              <PostHogMaskView>
                <Input
                  accessibilityLabel="Email address"
                  autoCapitalize="none"
                  autoComplete="email"
                  autoFocus
                  keyboardType="email-address"
                  onChangeText={setEmail}
                  onSubmitEditing={() => {
                    if (!pending && email.trim()) {
                      continueWithEmailMutation.mutate();
                    }
                  }}
                  placeholder="Email address"
                  testID="get-started-email"
                  value={email}
                />
              </PostHogMaskView>
              <Button
                className="h-12 rounded-full"
                disabled={pending || !email.trim()}
                onPress={() => continueWithEmailMutation.mutate()}
                testID="get-started-continue"
              >
                <Text>Continue</Text>
              </Button>
            </View>
          )}
        </View>
      ) : Platform.OS === "web" ? (
        <Button
          className="h-12 rounded-full"
          disabled={pending}
          onPress={revealEmail}
          testID="get-started-use-email"
          variant="outline"
        >
          <Icon className="text-foreground" name="FiMail" size={18} />
          <Text>{emailLabel}</Text>
        </Button>
      ) : (
        <Pressable
          accessibilityRole="link"
          className="items-center py-1"
          disabled={pending}
          onPress={revealEmail}
          testID="get-started-use-email"
        >
          <Text className="text-xs text-muted-foreground underline">{emailLabel}</Text>
        </Pressable>
      )}
      {socialOnly || Platform.OS === "web" ? null : (
        <Pressable
          accessibilityRole="button"
          className="items-center py-1"
          disabled={pending}
          onPress={() => continueWithoutAccountMutation.mutate()}
          testID="get-started-without-account"
        >
          <Text className="text-xs text-muted-foreground underline">Continue without an account</Text>
        </Pressable>
      )}
      {message ? (
        <Text accessibilityRole="alert" className="text-center text-sm text-destructive">
          {message}
        </Text>
      ) : null}
    </View>
  );
}
